import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, MenuItem, FormControlLabel, RadioGroup,
  Radio, Checkbox, Stack, Box, Typography, Divider
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { CustomDateTime, CustomInput } from "../../../../../../CustomForm";
import CustomTextArea from "../../../../../../CustomForm/CustomTextArea";
import { QK } from "../../../../../../../base/qk";
import { getIssueTypes } from "../../../../../../../api/services/ReferenceData/issueTypeService";
import { createBodyMap, updateBodyMap } from "../../../../../../../api/services/Assessment/BodyMap";
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import EditNoteIcon from '@mui/icons-material/EditNote';

const FRONT_BODY_PARTS = [
  "Head", "Face", "Chest", "Abdomen", "Left Arm", "Right Arm",
  "Left Hand", "Right Hand", "Left Leg", "Right Leg", "Left Foot", "Right Foot",
];

const BACK_BODY_PARTS = [
  "Back of Head", "Neck", "Upper Back", "Lower Back", "Left Arm Back", "Right Arm Back",
  "Left Hand Back", "Right Hand Back", "Left Leg Back", "Right Leg Back", "Left Foot Back", "Right Foot Back",
];

const FrontBodyImage = ({ selectedPart, onPartClick }) => {
  const [svgContent, setSvgContent] = useState(null);

  useEffect(() => {
    fetch('/images/front-body-map.svg')
      .then(response => response.text())
      .then(svg => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const svgElement = doc.querySelector('svg');
        
        const parts = svgElement.querySelectorAll('.body-part');
        parts.forEach(part => {
          const partName = part.getAttribute('data-part');
          if (selectedPart === partName) {
            part.classList.add('selected');
          } else {
            part.classList.remove('selected');
          }
          part.addEventListener('click', () => {
            if (onPartClick) onPartClick(partName);
          });
        });
        
        setSvgContent(svgElement.outerHTML);
      })
      .catch(error => console.error('Error loading front body map:', error));
  }, [selectedPart, onPartClick]);

  useEffect(() => {
    if (svgContent) {
      const container = document.getElementById('front-body-container');
      if (container) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svgElement = doc.querySelector('svg');
        
        const parts = svgElement.querySelectorAll('.body-part');
        parts.forEach(part => {
          const partName = part.getAttribute('data-part');
          if (selectedPart === partName) {
            part.classList.add('selected');
          } else {
            part.classList.remove('selected');
          }
          part.addEventListener('click', () => {
            if (onPartClick) onPartClick(partName);
          });
        });
        
        container.innerHTML = '';
        container.appendChild(svgElement);
      }
    }
  }, [svgContent, selectedPart, onPartClick]);

  return <div id="front-body-container" />;
};

const BackBodyImage = ({ selectedPart, onPartClick }) => {
  const [svgContent, setSvgContent] = useState(null);

  useEffect(() => {
    fetch('/images/back-body-map.svg')
      .then(response => response.text())
      .then(svg => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const svgElement = doc.querySelector('svg');
        
        const parts = svgElement.querySelectorAll('.body-part');
        parts.forEach(part => {
          const partName = part.getAttribute('data-part');
          if (selectedPart === partName) {
            part.classList.add('selected');
          } else {
            part.classList.remove('selected');
          }
          part.addEventListener('click', () => {
            if (onPartClick) onPartClick(partName);
          });
        });
        
        setSvgContent(svgElement.outerHTML);
      })
      .catch(error => console.error('Error loading back body map:', error));
  }, [selectedPart, onPartClick]);

  useEffect(() => {
    if (svgContent) {
      const container = document.getElementById('back-body-container');
      if (container) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svgElement = doc.querySelector('svg');
        
        const parts = svgElement.querySelectorAll('.body-part');
        parts.forEach(part => {
          const partName = part.getAttribute('data-part');
          if (selectedPart === partName) {
            part.classList.add('selected');
          } else {
            part.classList.remove('selected');
          }
          part.addEventListener('click', () => {
            if (onPartClick) onPartClick(partName);
          });
        });
        
        container.innerHTML = '';
        container.appendChild(svgElement);
      }
    }
  }, [svgContent, selectedPart, onPartClick]);

  return <div id="back-body-container" />;
};

const BodyMapDialog = ({ open, handleClose, clientID, initialData = null, onSubmit }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const defaultState = {
    location_front: "",
    location_back: "",
    issue_type_id: "",
    issue: "",
    status: "active",
    notes: "",
    share_with_handover: false,
    share_with_manager: false,
    date_time: new Date().toISOString().slice(0, 16),
  };

  const [formData, setFormData] = useState(defaultState);

  useEffect(() => {
    if (initialData) {
      setFormData({
        location_front: FRONT_BODY_PARTS.includes(initialData.location) ? initialData.location : "",
        location_back: BACK_BODY_PARTS.includes(initialData.location) ? initialData.location : "",
        client: initialData.client,
        issue_type_id: initialData.issue_type?.id || "",
        issue: initialData.issue || "",
        status: initialData.status || "active",
        notes: initialData.notes || "",
        share_with_handover: initialData.share_with_handover || false,
        share_with_manager: initialData.share_with_manager || false,
        date_time: initialData.date_time
          ? new Date(initialData.date_time).toISOString().slice(0, 16)
          : new Date().toISOString().slice(0, 16),
      });
    } else {
      setFormData(defaultState);
    }
  }, [initialData, open]);

  const { data: issueTypes = [], isLoading: isIssueLoading } = useQuery(
    [QK.issueTypes, { noPagination: false }],
    () => getIssueTypes(1, false)
  );

  const mutation = useMutation(
    (payload) => (initialData ? updateBodyMap(initialData.id, payload) : createBodyMap(payload)),
    {
      onSuccess: () => {
        enqueueSnackbar(
          initialData ? "Body Map updated successfully!" : "Body Map added successfully!",
          { variant: "success" }
        );
        queryClient.invalidateQueries([QK.bodyMaps, clientID]);
        handleClose();
        setFormData(defaultState);
        if (onSubmit) onSubmit();
      },
      onError: () => {
        enqueueSnackbar(
          initialData ? "Failed to save Body Map" : "Failed to add Body Map",
          { variant: "error" }
        );
      },
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "location_front") {
      setFormData((prev) => ({ ...prev, location_front: value, location_back: value ? "" : prev.location_back }));
      return;
    }
    if (name === "location_back") {
      setFormData((prev) => ({ ...prev, location_back: value, location_front: value ? "" : prev.location_front }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handlePartClick = (part, view) => {
    if (view === 'front') {
      setFormData((prev) => ({ 
        ...prev, 
        location_front: part, 
        location_back: "" 
      }));
    } else {
      setFormData((prev) => ({ 
        ...prev, 
        location_back: part, 
        location_front: "" 
      }));
    }
  };

  const handleSubmit = () => {
    const final_location = formData.location_front || formData.location_back;
    const payload = {
      client: clientID,
      location: final_location,
      date_time: new Date(formData.date_time).toISOString(),
      ...formData,
    };
    mutation.mutate(payload);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '20px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box sx={{ 
            p: 1.5, borderRadius: '14px', 
            bgcolor: initialData ? 'rgba(234, 88, 12, 0.1)' : 'rgba(2, 132, 199, 0.1)',
            color: initialData ? '#ea580c' : '#0284c7',
            display: 'flex'
          }}>
            {initialData ? <EditNoteIcon /> : <AssignmentLateIcon />}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
              {initialData ? "Update Clinical Entry" : "New Body Map Entry"}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
              Click on body parts or select from dropdown
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: '#f8fafc', 
              borderRadius: '12px', 
              border: formData.location_front ? '2px solid #3b82f6' : '1px solid #e2e8f0',
              textAlign: 'center'
            }}>
              <FrontBodyImage 
                selectedPart={formData.location_front}
                onPartClick={(part) => handlePartClick(part, 'front')}
              />
              <CustomInput
                select 
                label="Front View Site" 
                name="location_front"
                value={formData.location_front} 
                onChange={handleChange}
                fullWidth 
                size="small" 
                disabled={!!formData.location_back}
                sx={{ mt: 1 }}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {FRONT_BODY_PARTS.map((part) => <MenuItem key={part} value={part}>{part}</MenuItem>)}
              </CustomInput>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: '#f8fafc', 
              borderRadius: '12px', 
              border: formData.location_back ? '2px solid #3b82f6' : '1px solid #e2e8f0',
              textAlign: 'center'
            }}>
              <BackBodyImage 
                selectedPart={formData.location_back}
                onPartClick={(part) => handlePartClick(part, 'back')}
              />
              <CustomInput
                select 
                label="Back View Site" 
                name="location_back"
                value={formData.location_back} 
                onChange={handleChange}
                fullWidth 
                size="small" 
                disabled={!!formData.location_front}
                sx={{ mt: 1 }}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {BACK_BODY_PARTS.map((part) => <MenuItem key={part} value={part}>{part}</MenuItem>)}
              </CustomInput>
            </Box>
          </Grid>

          <Grid size={12}>
            <CustomInput
              select 
              label="Hazard/Issue Category" 
              name="issue_type_id"
              value={formData.issue_type_id} 
              onChange={handleChange}
              fullWidth 
              size="small" 
              disabled={isIssueLoading}
            >
              <MenuItem value="">None</MenuItem>
              {issueTypes?.map((type) => (
                <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
              ))}
            </CustomInput>
          </Grid>

          <Grid size={12}>
            <CustomTextArea
              label="Observation Details" 
              name="issue"
              value={formData.issue} 
              onChange={handleChange}
              rows={3} 
              fullWidth
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ fontWeight: 900, color: '#475569', mb: 1, display: 'block' }}>
                RECORD STATUS
              </Typography>
              <RadioGroup row name="status" value={formData.status} onChange={handleChange}>
                <FormControlLabel 
                  value="active" 
                  control={<Radio size="small" />} 
                  label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Active</Typography>} 
                />
                <FormControlLabel 
                  value="inactive" 
                  control={<Radio size="small" />} 
                  label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Inactive</Typography>} 
                />
              </RadioGroup>
            </Box>
          </Grid>

          <Grid size={12}>
            <CustomTextArea 
              label="Internal Clinical Notes" 
              name="notes" 
              value={formData.notes} 
              onChange={handleChange} 
              rows={2} 
              fullWidth 
            />
          </Grid>

          <Grid size={12}>
            <CustomDateTime
              label="Incident/Observation Date" 
              name="date_time"
              value={formData.date_time}
              onChange={(val) => setFormData((prev) => ({ ...prev, date_time: val.target.value }))}
              fullWidth
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ mb: 1.5, opacity: 0.6 }} />
            <Typography variant="caption" sx={{ fontWeight: 900, color: '#64748b', mb: 1, display: 'block' }}>
              COMMUNICATION PERMISSIONS
            </Typography>
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Checkbox 
                    size="small" 
                    checked={formData.share_with_handover} 
                    onChange={handleChange} 
                    name="share_with_handover" 
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>Include in Handover</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    size="small" 
                    checked={formData.share_with_manager} 
                    onChange={handleChange} 
                    name="share_with_manager" 
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>Alert Manager</Typography>}
              />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={handleClose} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 800 }}>
          Discard
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disableElevation
          disabled={mutation.isLoading || !(formData.location_front || formData.location_back)}
          sx={{ 
            bgcolor: '#0f172a', 
            borderRadius: '10px', 
            px: 4, 
            py: 1, 
            textTransform: 'none', 
            fontWeight: 800, 
            '&:hover': { bgcolor: '#1e293b' } 
          }}
        >
          {mutation.isLoading ? "Syncing..." : initialData ? "Confirm Update" : "Finalize Entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BodyMapDialog;